import { FiAlertTriangle, FiInfo } from "react-icons/fi";
import { HiShieldCheck } from "react-icons/hi";

const THEMES = {
  ok: {
    // cores base
    grad: "from-emerald-600 via-teal-600 to-cyan-600",
    ring: "ring-emerald-300/50",
    chipBg: "bg-emerald-50",
    chipTx: "text-emerald-800",
    iconWrap: "bg-white/15",
    bullet: "bg-emerald-100 text-emerald-800",
    glow: "from-emerald-400/40 to-cyan-400/40",
  },
  warn: {
    grad: "from-amber-600 via-orange-600 to-yellow-600",
    ring: "ring-amber-300/50",
    chipBg: "bg-amber-50",
    chipTx: "text-amber-800",
    iconWrap: "bg-white/15",
    bullet: "bg-amber-100 text-amber-800",
    glow: "from-amber-400/40 to-orange-400/40",
  },
  stop: {
    grad: "from-rose-700 via-red-700 to-pink-700",
    ring: "ring-rose-300/50",
    chipBg: "bg-rose-50",
    chipTx: "text-rose-800",
    iconWrap: "bg-white/15",
    bullet: "bg-rose-100 text-rose-800",
    glow: "from-rose-400/40 to-red-400/40",
  },
  neutral: {
    grad: "from-slate-600 via-slate-600 to-slate-700",
    ring: "ring-slate-300/50",
    chipBg: "bg-slate-50",
    chipTx: "text-slate-800",
    iconWrap: "bg-white/15",
    bullet: "bg-slate-100 text-slate-800",
    glow: "from-slate-400/40 to-slate-400/40",
  },
};

const MAP = {
  "0005": {
    title: "Negociação Recomendada",
    desc: "Indicadores favoráveis para aprovação.",
    icon: HiShieldCheck,
    t: THEMES.ok,
  },
  "0004": {
    title: "Negociação cautelosa",
    desc: "Prosseguir com garantias/condições adicionais.",
    icon: FiAlertTriangle,
    t: THEMES.warn,
  },
  "0003": {
    title: "Negociação Não recomendada",
    desc: "Risco elevado — evitar concessão neste momento.",
    icon: HiShieldCheck,
    t: THEMES.stop,
  },
};

export default function DecisionBanner({
  flag,
  motivo,
  insights = [], // array de strings (opcional)
  rightNote, // string opcional (ex: "Motor de Crédito")
  className = "",
  score = null,
}) {
  const key = String(flag || "").padStart(4, "0");
  const isScoreIndisponivel = Number(score) == 2;
  const meta = isScoreIndisponivel
    ? {
        title: "Sugestão de negócio indisponível para esta empresa",
        desc: "Analise os dados deste relatório para informações mais detalhadas",
        icon: FiInfo,
        t: THEMES.neutral,
      }
    : MAP[key] || {
        title: "Sugestão de negócio indisponível para esta empresa",
        desc: "Analise os dados deste relatório para informações mais detalhadas",
        icon: FiInfo,
        t: THEMES.neutral,
      };
  const Icon = meta.icon;

  return (
    <section
      className={[
        "relative overflow-hidden rounded-3xl ring-1 shadow-sm text-white mt-5",
        "bg-gradient-to-r",
        meta.t.grad,
        meta.t.ring,
        className,
      ].join(" ")}
      role="status"
      aria-live="polite"
    >
      {/* Glow blobs (decor) */}
      <div
        className={`pointer-events-none absolute -top-24 -left-24 h-64 w-64 blur-3xl bg-gradient-to-br ${meta.t.glow}`}
      />
      <div
        className={`pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 blur-3xl bg-gradient-to-tr ${meta.t.glow}`}
      />

      {/* Watermark do código gigante */}
      <div className="pointer-events-none absolute right-6 bottom-2 select-none opacity-15 font-extrabold text-[80px] leading-none tracking-tighter">
        {key}
      </div>

      {/* Faixa diagonal */}
      <div className="absolute -right-16 -top-10 rotate-12 bg-white/10 backdrop-blur-sm h-44 w-80 rounded-2xl" />

      {/* Conteúdo */}
      <div className="relative p-5 md:p-6">
        <div className="flex items-start gap-4 md:gap-5">
          {/* Ícone duotone */}
          <div
            className={`shrink-0 rounded-2xl p-3 md:p-4 ${meta.t.iconWrap} ring-1 ring-white/20`}
          >
            <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" aria-hidden />
          </div>

          <div className="min-w-0 flex-1">
            {/* Cabeçalho */}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl md:text-2xl font-extrabold tracking-tight">
                {meta.title}
              </h3>

              {rightNote && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 ring-1 ring-white/25">
                  {rightNote}
                </span>
              )}
            </div>

            {/* Descrição */}
            <p className="mt-1 text-sm md:text-[15px] text-white/90 leading-relaxed">
              {motivo?.trim() ? motivo : meta.desc}
            </p>

            {/* Bullets/insights (opcional) */}
            {!!insights.length && (
              <div className="mt-3 flex flex-wrap gap-2">
                {insights.slice(0, 4).map((it, idx) => (
                  <span
                    key={idx}
                    className={`text-xs md:text-[13px] px-2.5 py-1 rounded-full ${meta.t.bullet} backdrop-blur-[1px] ring-1 ring-white/30`}
                  >
                    {it}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
